import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MenuHeading from '../MenuHeading';
import { getJournals, getVerboseIssues } from '../../services/firebaseService';
import { FaSearch } from 'react-icons/fa';
import { getJournalTypeDisplayName } from '../../config/journalTypes';
import { getArticlePageUrl } from '../../utils/journalUrlHelper';
import { getBestFileUrl } from '../../utils/fileUrlConverter';
import { formatArticlePages } from '../../utils/articlePages';
import { formatIssueRecord } from '../../utils/issueFormat';

const IssuesContent = ({ submenuKey }) => {
  const [journals, setJournals] = useState([]);

  // Standard public PDF address (/files/<name>.pdf)
  const getFileUrl = (article) => getBestFileUrl(article);

  // Utility function to check if article has a file
  const hasFile = (article) => {
    return article.file || article.fileLink || article.pdfLink;
  };

  // Utility function to find clean 0-based index of article within an issue
  const getArticleIndexInIssue = (art, issueList) => {
    if (art && art.articleNumber) return art.articleNumber;
    const found = (issueList || []).findIndex(j => (j.id && j.id === art.id) || (j.title && j.title === art.title));
    return found !== -1 ? found : 0;
  };
  
  const [issues, setIssues] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getJournals().then(setJournals);
    getVerboseIssues().then(setIssues);
  }, []);

  // Filter journals by search
  const filterJournals = (journals) => {
    if (!search.trim()) return journals;
    const s = search.toLowerCase();
    return journals.filter(j =>
      (j.title && j.title.toLowerCase().includes(s)) ||
      (j.authors && j.authors.toLowerCase().includes(s)) ||
      (j.volume && j.volume.toLowerCase().includes(s)) ||
      (j.issue && j.issue.toLowerCase().includes(s))
    );
  };

  // Helper to normalize volume/issue values
  const normalize = val => (val || '').toString().replace(/(issue|vol)[- ]*/i, '').trim().toLowerCase();

  // Helper to match journals to an issue
  const getJournalsForIssue = (issueObj) => {
    return filterJournals(journals).filter(j => {
      // Normalize and match both Vol and issue (case-insensitive, string/number tolerant)
      const journalVol = normalize(j.Vol || j.volume);
      const journalIssue = normalize(j.issue || j.Issue);
      const issueVol = normalize(issueObj.Vol);
      const issueNum = normalize(issueObj.issue);
      return journalVol === issueVol && journalIssue === issueNum;
    });
  };

  // Filter issues for current or archived
  const filteredIssues = issues.filter(issue =>
    submenuKey === 'current-issue' ? issue.isCurrentIssue :
    submenuKey === 'archives' ? issue.isArchived : true
  );

  // Sort issues by Vol and issue number
  filteredIssues.sort((a, b) => {
    const volA = parseInt((a.Vol || '').replace(/\D/g, ''));
    const volB = parseInt((b.Vol || '').replace(/\D/g, ''));
    if (volA !== volB) return volB - volA;
    const issueA = parseInt((a.issue || '').replace(/\D/g, ''));
    const issueB = parseInt((b.issue || '').replace(/\D/g, ''));
    return issueA - issueB;
  });

  return (
    <div className="animate-fadein relative">
      {/* Search Button & Input */}
      <div className="absolute right-0 top-0 z-10 flex items-center">
        <button
          className="bg-accent text-white p-2 rounded-full shadow hover:bg-accent-dark transition"
          onClick={() => setSearchOpen(v => !v)}
        >
          <FaSearch />
        </button>
        <div className={`transition-all duration-300 ml-2 ${searchOpen ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'}`}>
          <input
            type="text"
            className="px-3 py-2 rounded border border-accent focus:outline-none w-full"
            placeholder="Search by title, author, vol, issue..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      <MenuHeading title={submenuKey === 'current-issue' ? 'Current Issue' : 'Archives'} />
      {filteredIssues.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No issues found.</div>
      ) : (
        <div className="space-y-4">
          {filteredIssues.map(issue => {
            const journalsForThisIssue = getJournalsForIssue(issue);
            // Compose heading: {StartMonth}-{EndMonth} {Year} - Volume {Vol} - Issue {issue}
            const getMonthShort = (dateObj) => dateObj.toLocaleString('default', { month: 'short' });
            let rangeLabel = '';
            if (issue.createdAt && issue.validity) {
              let startDate, endDate;
              if (typeof issue.createdAt === 'object' && issue.createdAt.seconds) {
                startDate = new Date(issue.createdAt.seconds * 1000);
              } else {
                startDate = new Date(issue.createdAt);
              }
              if (typeof issue.validity === 'object' && issue.validity.seconds) {
                endDate = new Date(issue.validity.seconds * 1000);
              } else {
                endDate = new Date(issue.validity);
              }
              if (!isNaN(startDate) && !isNaN(endDate)) {
                const startMonth = getMonthShort(startDate);
                const endMonth = getMonthShort(endDate);
                const year = startDate.getFullYear();
                rangeLabel = `${startMonth}-${endMonth} ${year}`;
              }
            }
            const heading = `${rangeLabel} - Volume ${issue.Vol} - ${issue.issue}`;
            return (
              <div key={issue.id} className="rounded-lg bg-white shadow-sm py-2 mb-6 mt-4 w-full max-w-5xl mx-auto">
                {/* Header Card */}
                <div className="relative flex flex-col sm:flex-row items-start bg-gray-50 rounded p-4 mb-2">
                  {/* ISSN Top Right */}
                  {/* <div className="absolute right-4 top-4 text-xs font-semibold text-gray-600">{issn}</div> */}
                  {issue.coverPage && (
                    <img src={issue.coverPage} alt={heading} className="w-48 h-64 object-cover rounded border shadow mr-6 mb-4 sm:mb-0" />
                  )}
                  <div className="flex-1 w-full flex flex-col items-start justify-start">
                    {submenuKey === 'current-issue' && (
                      <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold mb-2">Current Issue</span>
                    )}
                    <div className="text-2xl font-bold text-gray-800 mb-1">{rangeLabel}</div>
                    <div className="text-lg font-semibold text-gray-700 mb-1">
                      <i className="bi bi-journal-bookmark-fill mr-2 text-accent"></i>
                      {formatIssueRecord(issue)}
                    </div>
                    <div className="flex flex-col gap-1 mb-2 ml-1 text-sm font-medium text-gray-700">
                      {/* <span>Number of Articles: {numJournals}</span> */}
                    </div>
                  </div>
                </div>
                
                {/* Journal Content - Displayed directly without accordion */}
                <div className="p-4">
                  {journalsForThisIssue.length === 0 ? (
                    <div className="text-gray-500">No journals found for this issue.</div>
                  ) : (
                    <div className="bg-white space-y-0">
                          <div className="bg-white space-y-0">
                            {/* Editorial Note */}
                            {journalsForThisIssue.filter(j => j.type === 'editorialNote').length > 0 && (
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-[#B2EBF2] px-3 py-2">
                                  <h3 className="text-[#03478E] font-bold text-center text-sm">{getJournalTypeDisplayName('editorialNote')}</h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {journalsForThisIssue
                                      .filter(j => j.type === 'editorialNote')
                                      .slice(0, 6)
                                      .map((article, idx) => (
                                        <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0">
                                          <Link
                                            to={getArticlePageUrl(article, getArticleIndexInIssue(article, journalsForThisIssue))}
                                            className="text-sm font-semibold text-gray-800 hover:text-[#03478E] hover:underline mb-1 leading-tight block"
                                          >
                                            {article.title}
                                          </Link>
                                          <div className="text-sm text-gray-600 mb-1 font-semibold">
                                            by {article.authors}
                                          </div>
                                          {formatArticlePages(article) && (
                                            <div className="text-xs text-gray-500 mb-1">{formatArticlePages(article)}</div>
                                          )}
                                          {hasFile(article) && (
                                            <a
                                              href={getFileUrl(article)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Philosophical Insights */}
                            {journalsForThisIssue.filter(j => j.type === 'philosophicalInsights').length > 0 && (
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-accent-light/20 px-3 py-2">
                                  <h3 className="text-[#03478E] font-bold text-center text-sm">{getJournalTypeDisplayName('philosophicalInsights')}</h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {journalsForThisIssue
                                      .filter(j => j.type === 'philosophicalInsights')
                                      .slice(0, 6)
                                      .map((article, idx) => (
                                        <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0">
                                          <Link
                                            to={getArticlePageUrl(article, getArticleIndexInIssue(article, journalsForThisIssue))}
                                            className="text-sm font-semibold text-gray-800 hover:text-[#03478E] hover:underline mb-1 leading-tight block"
                                          >
                                            {article.title}
                                          </Link>
                                          <div className="text-sm text-gray-600 mb-1 font-semibold">
                                            by {article.authors}
                                          </div>
                                          {formatArticlePages(article) && (
                                            <div className="text-xs text-gray-500 mb-1">{formatArticlePages(article)}</div>
                                          )}
                                          {hasFile(article) && (
                                            <a
                                              href={getFileUrl(article)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Original Articles */}
                            {journalsForThisIssue.filter(j => j.type === 'originalArticles').length > 0 && (
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-accent-light/20 px-3 py-2">
                                  <h3 className="text-[#03478E] font-bold text-center text-sm">{getJournalTypeDisplayName('originalArticles')}</h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {journalsForThisIssue
                                      .filter(j => j.type === 'originalArticles')
                                      .slice(0, 8)
                                      .map((article, idx) => (
                                        <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0">
                                          <Link
                                            to={getArticlePageUrl(article, getArticleIndexInIssue(article, journalsForThisIssue))}
                                            className="text-sm font-semibold text-gray-800 hover:text-[#03478E] hover:underline mb-1 leading-tight block"
                                          >
                                            {article.title}
                                          </Link>
                                          <div className="text-sm text-gray-600 mb-1 font-semibold">
                                            by {article.authors}
                                          </div>
                                          {formatArticlePages(article) && (
                                            <div className="text-xs text-gray-500 mb-1">{formatArticlePages(article)}</div>
                                          )}
                                          {hasFile(article) && (
                                            <a
                                              href={getFileUrl(article)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Review Articles */}
                            {journalsForThisIssue.filter(j => j.type === 'reviewArticles').length > 0 && (
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-accent-light/20 px-3 py-2">
                                  <h3 className="text-[#03478E] font-bold text-center text-sm">{getJournalTypeDisplayName('reviewArticles')}</h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {journalsForThisIssue
                                      .filter(j => j.type === 'reviewArticles')
                                      .slice(0, 6)
                                      .map((article, idx) => (
                                        <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0">
                                          <Link
                                            to={getArticlePageUrl(article, getArticleIndexInIssue(article, journalsForThisIssue))}
                                            className="text-sm font-semibold text-gray-800 hover:text-[#03478E] hover:underline mb-1 leading-tight block"
                                          >
                                            {article.title}
                                          </Link>
                                          <div className="text-sm text-gray-600 mb-1 font-semibold">
                                            by {article.authors}
                                          </div>
                                          {formatArticlePages(article) && (
                                            <div className="text-xs text-gray-500 mb-1">{formatArticlePages(article)}</div>
                                          )}
                                          {hasFile(article) && (
                                            <a
                                              href={getFileUrl(article)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Seminar Report */}
                            {journalsForThisIssue.filter(j => j.type === 'seminarReport').length > 0 && (
                              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                                <div className="bg-accent-light/20 px-3 py-2">
                                  <h3 className="text-[#03478E] font-bold text-center text-sm">{getJournalTypeDisplayName('seminarReport')}</h3>
                                </div>
                                <div className="p-3">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {journalsForThisIssue
                                      .filter(j => j.type === 'seminarReport')
                                      .slice(0, 6)
                                      .map((article, idx) => (
                                        <div key={idx} className="border-b border-gray-100 pb-2 last:border-b-0">
                                          <Link
                                            to={getArticlePageUrl(article, getArticleIndexInIssue(article, journalsForThisIssue))}
                                            className="text-sm font-semibold text-gray-800 hover:text-[#03478E] hover:underline mb-1 leading-tight block"
                                          >
                                            {article.title}
                                          </Link>
                                          <div className="text-sm text-gray-800 mb-1 font-semibold">
                                            by {article.authors}
                                          </div>
                                          {formatArticlePages(article) && (
                                            <div className="text-xs text-gray-500 mb-1">{formatArticlePages(article)}</div>
                                          )}
                                          {hasFile(article) && (
                                            <a
                                              href={getFileUrl(article)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IssuesContent; 